import api from "./api";
import { resolveRoleName } from "../utils/auth";

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 10;

const normalizeProcess = (dto) => {
  // Spread all original fields to preserve everything from the API
  const normalized = {
    ...dto,
    process_id: dto?.process_id ?? dto?.id,
    process_code: dto?.process_code ?? dto?.code ?? "",
    process_name: dto?.process_name ?? dto?.name ?? "",
    company_id: dto?.company_id ?? dto?.company?.company_id ?? null,
    process_category_id:
      dto?.process_category_id ?? dto?.processCategoryId ?? null,
    version: dto?.version ?? null,
    created_at: dto?.created_at ?? dto?.createdAt ?? null,
    updated_at: dto?.updated_at ?? dto?.updatedAt ?? null,
    capacity_requirement_minutes: dto?.capacity_requirement_minutes ?? null,
    process_overview: dto?.process_overview ?? dto?.overview ?? "",
    status: dto?.status ?? null,
  };
  return normalized;
};

const extractProcessArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const START_EVENT_GATEWAY_ID = "__START_EVENT__";
const GATEWAY_PLACEMENT_PREFIX = "__GW__";
const toGatewayTaskId = (value) => {
  const s = String(value);
  // Start Event and gateway-after-gateway placements are not task ids.
  if (s === START_EVENT_GATEWAY_ID || s.startsWith(GATEWAY_PLACEMENT_PREFIX)) {
    return null;
  }
  return value != null && value !== "" ? Number(value) : null;
};

// Resolve a gateway-after-gateway placement ("__GW__<local id>" stored in
// afterTaskId) to the referenced gateway's ARRAY POSITION, which the backend
// persists as after_gateway_id (afterGatewayIndex -> after_gateway_id).
const resolveAfterGatewayIndex = (g, arr) => {
  // Already-resolved array position (the agent's save wire emits it directly).
  const direct = g.afterGatewayIndex ?? g.after_gateway_index;
  if (direct != null && direct !== "") return Number(direct);
  const at = String(g.afterTaskId ?? g.after_task_id ?? "");
  if (!at.startsWith(GATEWAY_PLACEMENT_PREFIX)) return undefined;
  const ref = at.slice(GATEWAY_PLACEMENT_PREFIX.length);
  const idx = (arr || []).findIndex(
    (x) => String(x.id ?? x.gateway_pk_id) === ref,
  );
  return idx >= 0 ? idx : undefined;
};

// Map one editor branch to the backend gateway-branch shape. Preserves
// isDefault and (for task-targeting branches) connectToEnd, and detects a named
// end event by its endEventName — a named end event has no endTaskId, so it must
// not be misread as a regular branch (which would drop its name).
// Resolve a gateway's join → split reference (a target gateway's local id) to
// the target's ARRAY POSITION within the submitted gateways array, which is how
// the backend links join → split (convergeAtGatewayIndex → converge_at_gateway_id).
const resolveConvergeAtGatewayIndex = (g, arr) => {
  if (g.convergeAtGatewayIndex != null) return g.convergeAtGatewayIndex;
  // Already-resolved array position (the agent's save wire emits it directly).
  if (g.converge_at_gateway_index != null) return Number(g.converge_at_gateway_index);
  const ref = g.convergeAtGatewayId;
  if (ref == null || ref === "") return undefined;
  const idx = (arr || []).findIndex(
    (x) => String(x.id ?? x.gateway_pk_id) === String(ref),
  );
  return idx >= 0 ? idx : undefined;
};

// Empty string must NOT become Number("") === 0 — that made the backend treat
// an unselected converge point as task id 0 / first task and misplace joins.
const toConvergeAtTaskId = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const resolveConvergeAtTaskId = (g) => {
  const raw =
    g?.convergeAtTaskId != null && g.convergeAtTaskId !== ""
      ? g.convergeAtTaskId
      : g?.converge_at_task_id != null && g.converge_at_task_id !== ""
        ? g.converge_at_task_id
        : null;
  return toConvergeAtTaskId(raw);
};

const mapBranchForGatewaySave = (b, allGateways) => {
  const endName = b.endEventName || b.end_event_name || "";
  const endTaskRaw = b.endTaskId ?? b.end_task_id;
  const targetRaw = b.targetTaskId ?? b.target_task_id;
  const targetGatewayIndex = b.targetGatewayIndex ?? b.target_gateway_index;
  const rawBranchPk = b.branch_pk_id ?? b.branchPkId ?? b._backendId;
  const branchPkId =
    rawBranchPk != null &&
    rawBranchPk !== "" &&
    Number.isInteger(Number(rawBranchPk))
      ? Number(rawBranchPk)
      : undefined;
  // FBPM Ch.7 branch probability — analyst-owned measured data; must survive
  // every save shape (dropping it reverts the saved version to equal-split).
  const probability =
    b.probability != null && b.probability !== "" ? Number(b.probability) : undefined;

  const withBranchPk = (branch) => {
    const out =
      branchPkId != null ? { ...branch, branch_pk_id: Number(branchPkId) } : branch;
    return probability != null ? { ...out, probability } : out;
  };

  // Some callers pre-normalize "__GW__<localId>" into targetGatewayIndex before
  // this shared service runs. Preserve that value so branch -> gateway targets
  // survive the final request body rebuild.
  if (targetGatewayIndex != null && targetGatewayIndex !== "") {
    const gwBranch = {
      targetTaskId: null,
      targetGatewayIndex: Number(targetGatewayIndex),
      isDefault: !!(b.isDefault ?? b.is_default),
    };
    if (b.condition) gwBranch.condition = b.condition;
    return withBranchPk(gwBranch);
  }

  // Branch → gateway: target is "__GW__<localId>"; resolve to the gateway's array
  // position within the submitted gateways array (backend persists it as
  // gateway_branch.target_gateway_id).
  if (typeof targetRaw === "string" && targetRaw.startsWith("__GW__")) {
    const ref = targetRaw.slice("__GW__".length);
    const idx = (allGateways || []).findIndex(
      (x) => String(x.id ?? x.gateway_pk_id) === String(ref),
    );
    const gwBranch = {
      targetTaskId: null,
      isDefault: !!(b.isDefault ?? b.is_default),
    };
    if (idx >= 0) gwBranch.targetGatewayIndex = idx;
    if (b.condition) gwBranch.condition = b.condition;
    return withBranchPk(gwBranch);
  }
  const hasTarget = targetRaw != null && targetRaw !== "";
  const hasEndTask = endTaskRaw != null && endTaskRaw !== "";
  const isEndpoint =
    !hasTarget && (String(endName).trim() !== "" || hasEndTask);
  const isDefault = !!(b.isDefault ?? b.is_default);
  if (isEndpoint) {
    const endpoint = {
      endTaskId: hasEndTask ? Number(endTaskRaw) : null,
      endEventName: endName || "",
      isDefault,
    };
    if (b.condition) endpoint.condition = b.condition;
    return withBranchPk(endpoint);
  }
  const branch = {
    targetTaskId: hasTarget ? Number(targetRaw) : null,
    isDefault,
  };
  if (b.condition) branch.condition = b.condition;
  // connect_to_end only applies to a branch that targets a task (a chain).
  if (hasTarget) branch.connectToEnd = (b.connectToEnd ?? b.connect_to_end) !== false;
  return withBranchPk(branch);
};

const normalizeRoleKey = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "");

const getCurrentAuthUser = () => {
  try {
    const raw = sessionStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;
    if (auth?.user) return auth.user;
  } catch {}

  try {
    const raw = localStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;
    if (auth?.user) return auth.user;
  } catch {}

  return null;
};

async function getCurrentUserRoleKey() {
  const user = getCurrentAuthUser();
  if (!user) return "";
  const roleName = await resolveRoleName(user);
  return normalizeRoleKey(roleName);
}

const toQuery = ({ page, limit, search } = {}) => {
  const query = new URLSearchParams();
  if (page != null) query.append("page", String(page));
  if (limit != null) query.append("limit", String(limit));
  if (String(search || "").trim()) {
    query.append("search", String(search).trim());
  }
  return query.toString();
};

const extractMeta = (payload, page = DEFAULT_PAGE, limit = DEFAULT_LIMIT) => {
  if (payload?.meta && typeof payload.meta === "object") {
    return {
      total: Number(payload.meta.total) || 0,
      page: Number(payload.meta.page) || Number(page) || DEFAULT_PAGE,
      limit: Number(payload.meta.limit) || Number(limit) || DEFAULT_LIMIT,
      totalPages: Number(payload.meta.totalPages) || 1,
    };
  }
  const data = extractProcessArray(payload);
  return {
    total: data.length,
    page: Number(page) || DEFAULT_PAGE,
    limit: Number(limit) || DEFAULT_LIMIT,
    totalPages: 1,
  };
};

export async function getProcesses() {
  const res = await api.get("/process");
  return extractProcessArray(res).map(normalizeProcess);
}

export async function getProcessesPaginated({
  page = DEFAULT_PAGE,
  limit = DEFAULT_LIMIT,
  search = "",
} = {}) {
  const qs = toQuery({ page, limit, search });
  const roleKey = await getCurrentUserRoleKey();
  const endpoint =
    roleKey === "SUPERADMIN"
      ? "/process"
      : "/hierarchical-view/relation/process/bulk";
  const res = await api.get(`${endpoint}${qs ? `?${qs}` : ""}`);

  const data = extractProcessArray(res).map(normalizeProcess);
  return {
    data,
    meta: extractMeta(res, page, limit),
  };
}

export async function getProcessesByCompanyId(companyId) {
  if (!companyId) throw new Error("Company ID is required");
  try {
    const res = await api.get(`/process/company/${companyId}`);
    return extractProcessArray(res).map(normalizeProcess);
  } catch {
    const res = await api.get(`/process/companyId/${companyId}`);
    return extractProcessArray(res).map(normalizeProcess);
  }
}

export async function getProcessInformation() {
  const res = await api.get("/process/information");
  const arr = extractProcessArray(res);
  return arr.map((item) => ({
    process_id: item?.process_id ?? item?.id,
    process_name: item?.process_name ?? item?.name ?? "",
    process_code: item?.process_code ?? item?.code ?? "",
    process_version: item?.process_version ?? item?.version ?? null,
    company: item?.company || null,
    company_id: item?.company?.company_id ?? item?.company_id ?? null,
    is_complete: item?.is_complete ?? null,
    reason: item?.reason ?? null,
    // Advisory skill-match signal (responsible job vs task skills) — distinct from
    // is_complete (data completeness); same rule as GET /process/:id/skill-check.
    skill_gap_count: item?.skill_gap_count ?? 0,
    skill_gap_tasks: Array.isArray(item?.skill_gap_tasks) ? item.skill_gap_tasks : [],
  }));
}

export async function updateProcess(id, payload) {
  if (id == null) throw new Error("Process id is required");
  const body = {
    process_name: String(payload.process_name || payload.name || "").trim(),
    process_code: String(payload.process_code || payload.code || "").trim(),
    process_version:
      payload.process_version != null
        ? Number(payload.process_version)
        : payload.version != null
          ? Number(payload.version)
          : payload.process_version === null
            ? null
            : undefined,
    company_id: payload.company_id != null ? Number(payload.company_id) : null,
    process_status_id:
      payload.process_status_id != null
        ? Number(payload.process_status_id)
        : payload.process_status_id === null
          ? null
          : undefined,
    process_category_id:
      payload.process_category_id != null
        ? Number(payload.process_category_id)
        : payload.process_category_id === null
          ? null
          : undefined,
    parent_process_id:
      payload.parent_process_id != null
        ? Number(payload.parent_process_id)
        : null,
    // Demand for the queueing layer (FBPM Ch.7 §7.2): cases arriving per week.
    ...(payload.case_arrival_rate !== undefined
      ? {
          case_arrival_rate:
            payload.case_arrival_rate === null || payload.case_arrival_rate === ""
              ? null
              : Number(payload.case_arrival_rate),
        }
      : {}),
    process_overview: String(
      payload.process_overview || payload.overview || "",
    ),
    workflow: Array.isArray(payload.workflow)
      ? payload.workflow.map((w, idx) => {
          const item = { order: w?.order != null ? Number(w.order) : idx + 1 };
          if (w?.task_id != null) item.task_id = Number(w.task_id);
          if (w?.job_id != null) item.job_id = Number(w.job_id);
          if (w?.child_process_id != null)
            item.child_process_id = Number(w.child_process_id);
          return item;
        })
      : [],
    gateways: Array.isArray(payload.gateways)
      ? payload.gateways.map((g) => {
          const rawGatewayPk = g.gateway_pk_id ?? g.gatewayPkId ?? g._backendId;
          const gatewayPkId =
            rawGatewayPk != null &&
            rawGatewayPk !== "" &&
            Number.isInteger(Number(rawGatewayPk))
              ? Number(rawGatewayPk)
              : undefined;
          return {
            gateway_pk_id: gatewayPkId,
            type: String(g.type || "").toUpperCase(),
            name: g.name || "",
            afterTaskId:
              g.afterTaskId != null
                ? toGatewayTaskId(g.afterTaskId)
                : g.after_task_id != null
                  ? toGatewayTaskId(g.after_task_id)
                  : null,
            convergeAtTaskId: resolveConvergeAtTaskId(g),
            convergeGatewayName:
              g.convergeGatewayName || g.converge_gateway_name || "",
            convergeToEnd: !!(g.convergeToEnd ?? g.converge_to_end),
            convergeAtGatewayIndex: resolveConvergeAtGatewayIndex(
              g,
              payload.gateways,
            ),
            afterGatewayIndex: resolveAfterGatewayIndex(g, payload.gateways),
            branches:
              Array.isArray(g.branches) ?
                g.branches.map((b) =>
                  mapBranchForGatewaySave(b, payload.gateways),
                )
              : [],
          };
        })
      : [],
  };
  return api.patch(`/process/${id}`, body);
}

export async function deleteProcess(id) {
  if (id == null) throw new Error("Process id is required");
  return api.delete(`/process/${id}`);
}

export async function getProcessWithRelations(id) {
  if (id == null) throw new Error("Process id is required");
  const res = await api.get(`/process/${id}/with-relations`);
  if (
    res &&
    !Array.isArray(res.process_tasks) &&
    Array.isArray(res.process_task)
  ) {
    return { ...res, process_tasks: res.process_task };
  }
  return res;
}

export async function getProcessPaths(id) {
  if (id == null) throw new Error("Process id is required");
  return api.get(`/process/${id}/paths`);
}

export async function getProcessCapiData(id) {
  if (id == null) throw new Error("Process id is required");
  return api.get(`/process/${id}/capi-data`);
}

export async function createProcess(payload, options = {}) {
  // Build body according to provided schema
  const body = {
    process_name: String(payload.process_name || payload.name || "").trim(),
    process_code: String(payload.process_code || payload.code || "").trim(),
    process_version:
      payload.process_version != null
        ? Number(payload.process_version)
        : payload.version != null
          ? Number(payload.version)
          : payload.process_version === null
            ? null
            : undefined,
    company_id: payload.company_id != null ? Number(payload.company_id) : null,
    process_status_id:
      payload.process_status_id != null
        ? Number(payload.process_status_id)
        : payload.process_status_id === null
          ? null
          : undefined,
    process_category_id:
      payload.process_category_id != null
        ? Number(payload.process_category_id)
        : payload.process_category_id === null
          ? null
          : undefined,
    parent_process_id:
      payload.parent_process_id != null
        ? Number(payload.parent_process_id)
        : null,
    process_overview: String(
      payload.process_overview || payload.overview || "",
    ),
    workflow: Array.isArray(payload.workflow)
      ? payload.workflow.map((w, idx) => {
          const item = {};
          if (w?.task_id != null) item.task_id = Number(w.task_id);
          if (w?.job_id != null) item.job_id = Number(w.job_id);
          if (w?.child_process_id != null)
            item.child_process_id = Number(w.child_process_id);
          if (w?.order != null) item.order = Number(w.order);
          else item.order = idx + 1;
          return item;
        })
      : [],
    gateways: Array.isArray(payload.gateways)
      ? payload.gateways.map((g) => ({
          type: String(g.type || "").toUpperCase(),
          name: g.name || "",
          afterTaskId:
            g.afterTaskId != null
              ? toGatewayTaskId(g.afterTaskId)
              : g.after_task_id != null
                ? toGatewayTaskId(g.after_task_id)
                : null,
          convergeAtTaskId: resolveConvergeAtTaskId(g),
          convergeGatewayName:
            g.convergeGatewayName || g.converge_gateway_name || "",
          convergeToEnd: !!(g.convergeToEnd ?? g.converge_to_end),
          convergeAtGatewayIndex: resolveConvergeAtGatewayIndex(
            g,
            payload.gateways,
          ),
          afterGatewayIndex: resolveAfterGatewayIndex(g, payload.gateways),
          branches: Array.isArray(g.branches)
            ? g.branches.map((b) => mapBranchForGatewaySave(b, payload.gateways))
            : [],
        }))
      : [],
  };
  return api.post("/process", body, options);
}

export async function getProcessesByUsersBulk(userIds = []) {
  try {
    const raw = sessionStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;
    const currentUser = auth?.user;
    if (currentUser) {
      const rn = await resolveRoleName(currentUser);
      const norm = String(rn || "")
        .toUpperCase()
        .replace(/[^A-Z]/g, "");
      if (norm === "SUPERADMIN") {
        const res = await getProcesses();
        return Array.isArray(res) ? res.map(normalizeProcess) : [];
      }
    }
  } catch {}
  const payload = {
    userIds: (userIds || [])
      .map((x) => Number(x))
      .filter((n) => Number.isInteger(n)),
  };
  if (payload.userIds.length === 0) return [];
  const key = payload.userIds
    .slice()
    .sort((a, b) => a - b)
    .join(",");
  if (!getProcessesByUsersBulk._inflight)
    getProcessesByUsersBulk._inflight = new Map();
  if (getProcessesByUsersBulk._inflight.has(key)) {
    return await getProcessesByUsersBulk._inflight.get(key);
  }
  const p = (async () => {
    const res = await api.post(
      "/hierarchical-view/relation/process/bulk",
      payload,
    );
    const results = Array.isArray(res?.results) ? res.results : [];
    const set = new Map();
    for (const item of results) {
      if (!item || item.success !== true) continue;
      const data = item.data || {};
      const assoc = Array.isArray(data.associatedProcesses)
        ? data.associatedProcesses
        : [];
      for (const pr of assoc) {
        const norm = normalizeProcess(pr);
        if (norm.process_id != null) set.set(Number(norm.process_id), norm);
      }
      const created = Array.isArray(data.createdProcesses)
        ? data.createdProcesses
        : [];
      for (const pr of created) {
        const norm = normalizeProcess(pr);
        if (norm.process_id != null) set.set(Number(norm.process_id), norm);
      }
    }
    return Array.from(set.values());
  })();
  getProcessesByUsersBulk._inflight.set(key, p);
  try {
    return await p;
  } finally {
    getProcessesByUsersBulk._inflight.delete(key);
  }
}

// Submit a new process version for approval (Normal_User path)
export async function requestProcessVersion(processId, payload = {}) {
  if (processId == null) throw new Error("Process id is required");
  const body = {
    submitted_by:
      payload.submitted_by != null ? Number(payload.submitted_by) : undefined,
    process_name: payload.process_name,
    process_code: payload.process_code,
    process_overview: payload.process_overview,
    workflow: Array.isArray(payload.workflow)
      ? payload.workflow.map((w, idx) => {
          const item = { order: w?.order != null ? Number(w.order) : idx + 1 };
          if (w?.item_type) item.item_type = String(w.item_type).toLowerCase();
          if (w?.task_id != null) item.task_id = Number(w.task_id);
          if (w?.job_id != null) item.job_id = Number(w.job_id);
          if (w?.child_process_id != null)
            item.child_process_id = Number(w.child_process_id);
          return item;
        })
      : undefined,
    gateways: Array.isArray(payload.gateways)
      ? payload.gateways.map((g) => ({
          type: String(g.type || g.gateway_type || "").toUpperCase(),
          name: g.name || "",
          afterTaskId:
            g.afterTaskId != null
              ? toGatewayTaskId(g.afterTaskId)
              : g.after_task_id != null
                ? toGatewayTaskId(g.after_task_id)
                : null,
          convergeAtTaskId: resolveConvergeAtTaskId(g),
          convergeGatewayName:
            g.convergeGatewayName || g.converge_gateway_name || "",
          convergeToEnd: !!(g.convergeToEnd ?? g.converge_to_end),
          convergeAtGatewayIndex: resolveConvergeAtGatewayIndex(
            g,
            payload.gateways,
          ),
          afterGatewayIndex: resolveAfterGatewayIndex(g, payload.gateways),
          branches: Array.isArray(g.branches)
            ? g.branches.map((b) => mapBranchForGatewaySave(b, payload.gateways))
            : [],
        }))
      : undefined,
    parent_process_id:
      payload.parent_process_id != null
        ? Number(payload.parent_process_id)
        : undefined,
    parent_task_id:
      payload.parent_task_id != null
        ? Number(payload.parent_task_id)
        : undefined,
    capacity_requirement_minutes:
      payload.capacity_requirement_minutes != null
        ? Number(payload.capacity_requirement_minutes)
        : undefined,
    process_status_id:
      payload.process_status_id != null
        ? Number(payload.process_status_id)
        : undefined,
    process_status: payload.process_status,
    process_category_id:
      payload.process_category_id != null
        ? Number(payload.process_category_id)
        : undefined,
  };

  return api.post(`/process/${processId}/createNewVersion`, body);
}

// Review a pending process permission (ADMIN/SUPER_ADMIN path)
export async function reviewProcessPermission(permissionId, payload = {}) {
  if (permissionId == null) throw new Error("Permission id is required");
  const body = {
    permission_status: payload.permission_status,
    remarks: payload.remarks,
  };
  return api.patch(`/process/permission/${permissionId}/review`, body);
}

// List process permission requests (ADMIN/SUPER_ADMIN scoped in backend)
export async function getProcessPermissions(params = {}) {
  const query = new URLSearchParams();
  [
    "permission_status",
    "company_id",
    "submitted_by",
    "reviewed_by",
    "search",
    "page",
    "limit",
  ].forEach((key) => {
    const val = params[key];
    if (val !== undefined && val !== null && val !== "") {
      query.append(key, val);
    }
  });
  const qs = query.toString();
  return api.get(`/process/permissions${qs ? `?${qs}` : ""}`);
}

// Fetch single permission request by id
export async function getProcessPermissionById(permissionId) {
  if (permissionId == null) throw new Error("Permission id is required");
  return api.get(`/process/permissions/${permissionId}`);
}

// Permission counts (total/pending/approved/rejected)
export async function getProcessPermissionsCount() {
  return api.get(`/process/permissions/count`);
}
